import React from 'react';
import Box from '@mui/material/Box';
import { AddMenuButton, MuiCardCommonProps } from '../MuiCardBase/MuiCardBase';
import { borderRadius, dashboardCardDimensions, palette, spaces } from '../../../../assets/styles/themes/tokens';
import { colors } from '../../../../assets/styles/themes/variables';
import { CoverImage } from '../MuiCardBase/MuiCardBaseCover';
import FeatureStatusChip from '../MuiCardBase/FeatureStatusChip';
import { Icon, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import { getCardAriaLabel } from '../../../../utils/getCardAriaLabel';
import Tooltip from '../../../Tooltip';
import { Link } from 'found';
import { DashboardSection, routes } from '../../../../constants';
import { notifyBugsnagHandledError } from '../../../../utils/bugsnagClient';
import { getCardLinkProps } from '../../../../utils/searchUtils';
import useRouter from '../../../../utils/useRouter';
import getPublisherLogo from '../../../../assets/publisher/svg';
import { boxShadows } from '../../../../assets/styles/themes/tokens';

interface DashboardMuiCardProps extends MuiCardCommonProps  {
  contentType: 'video' | 'document' | 'product' | 'course';
  isVideo?: boolean;
  redirectToMindItemId?: string;
  dashboardSection?: DashboardSection;
}

const DashboardMuiCard: React.FC<DashboardMuiCardProps> = (props) => {
  const  { 
    id,
    contentType,
    isNew,
    itemData,
    isPinned,
    cover,
    publisher,
    redirectToMindItemId,
    isVideo,
    title,
    dashboardSection,
  } = props;
  const { t } = useTranslation();
  const { match } = useRouter();

  const getContentTypeText = () => {
    switch (contentType) {
      case 'document':
        return t('Unit');
      case 'product':
        return t('Product');
      case 'video':
        return t('Video');
      case 'course':
        return t((itemData as { course: { type: string } }).course.type);
      default:
        notifyBugsnagHandledError(new Error(`Unknown ${id} content type: ${contentType}`));
        return '';
    }
  };

  const getItemLink = () => {
    switch (contentType) {
      case 'product':
        return `/${routes.productPage.root}/${id}`;
      case 'document':
        return `/${routes.reader.root}/${id}`;
      case 'video':
        return `/${routes.reader.root}/${id}`;
      case 'course':
        return `/${routes.courseCatalog.root}/${id}`;
      default:
        notifyBugsnagHandledError(new Error(`Unknown ${id} content type: ${contentType}`));
        return '';
    }
  };

  const itemLinkTo = getCardLinkProps(
    redirectToMindItemId ? `/${routes.reader.root}/${redirectToMindItemId}` : getItemLink(),
    match.location.pathname,
    undefined,
    { dashboardSection },
  );

  return (
    <Box 
      sx={{
        height: isVideo ? dashboardCardDimensions.video.height : dashboardCardDimensions.height,
        width: isVideo ? dashboardCardDimensions.video.width : dashboardCardDimensions.width,        
        border: `1px solid ${colors.neutral200}`,
        borderRadius: borderRadius.lg,
        padding: spaces.sm,
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.md,
      }}
      data-testid='dashboard-mui-card'
    >
      <Box data-testid='dashboard-mui-card-thumbnail-container' sx={{ position: 'relative' }}>
        { isNew && <FeatureStatusChip size='medium' sx={{ top: `-${spaces.xxs}`, left: `-${spaces.xxs}` }} /> }
        <AddMenuButton 
          id={id} 
          itemData={itemData} 
          isPinned={isPinned}
          type={contentType} 
          size='large'
          buttonSx={{
            position: 'absolute',
            top: `-${spaces.xxs}`,
            right: `-${spaces.xxs}`,
            backgroundColor: `${palette.background.paperElevation1}!important`,
            '&.active': {
              backgroundColor: `${palette.background.paperElevation1}!important`,
            },
            '&.hover': {
              backgroundColor: `${palette.background.paperElevation1}!important`,
            },
            border: `1px solid ${palette.elevation.outlined}`,
            borderRadius: borderRadius.md,
          }}
        />
        <Link
          to={itemLinkTo}
          aria-label={getCardAriaLabel(contentType, title, t)}
        >
          <CoverImage 
            cover={cover}
            title={title}
            customSx={{
              height: isVideo ? 
                dashboardCardDimensions.video.coverImageHeight : dashboardCardDimensions.coverImageHeight,
              width: isVideo ? dashboardCardDimensions.video.coverImageWidth : dashboardCardDimensions.coverImageWidth,
              ':hover': {
                boxShadow: boxShadows.elevation6,
              }
            }}
          />
        </Link>
      </Box>
      <Box data-testid='dashboard-mui-card-metadata-container'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', flexAlign: 'center' }}>
          <Typography
            variant='overline'
            color='text.secondary'
            textTransform='uppercase'
            sx={{ flexShrink: 0 }}
          >
            {getContentTypeText()}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ paddingX: spaces.sm }}>•</Typography>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Icon 
              component={getPublisherLogo(publisher)} 
              sx={{ height: 32, width: 42, position: 'absolute', left: 0, top: '-8px' }}
            />
          </Box>
        </Box>
        <Box>
          <Link
            to={itemLinkTo}
            data-testid='dashboard-mui-card-title-link'>
            <Tooltip label={props.title} placement='top' hideWithoutOverflow={true}>
              <Typography
                data-testid='dashboard-mui-card-title'
                variant='h6'
                sx={{
                  ...getTextTruncationSx(isVideo ? 1 : 2),
                  color: palette.text.primary,
                  transition: 'color 300ms ease-out',
                  ':hover': {
                    color: colors.blue500,
                  },
                }}
              >
                {props.title}
              </Typography>
            </Tooltip>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardMuiCard;
